import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Target, Award, ChevronRight, Brain } from 'lucide-react';
import { BASE_URL } from '../config/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [testCode, setTestCode] = useState('');
  const [testError, setTestError] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const tokenType = localStorage.getItem('tokenType');
      
      if (!token || !tokenType) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}/api/auth/check`, {
          headers: {
            'Authorization': `${tokenType} ${token}`
          }
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          // Clear invalid tokens
          localStorage.removeItem('token');
          localStorage.removeItem('tokenType');
          localStorage.removeItem('userId');
          localStorage.removeItem('userEmail');
          setIsAuthenticated(false);
        }
      } catch (err) {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const handleAnswerClick = (index) => {
    setSelectedAnswer(index);
    setShowFeedback(true);
  };

  const getButtonClassName = (index) => {
    const baseClass = "py-2 rounded-md transition-colors duration-300";
    if (!showFeedback) {
      return `${baseClass} bg-purple-500 text-white hover:bg-purple-600`;
    }
    if (index === 3) { // Risk is the correct answer
      return `${baseClass} ${selectedAnswer === index ? 'bg-green-500 text-white' : 'bg-purple-500 text-white'}`;
    }
    return `${baseClass} ${selectedAnswer === index ? 'bg-red-500 text-white' : 'bg-purple-500 text-white'}`;
  };

  const handleStartTest = async () => {
    if (!testCode.trim()) {
      setTestError('Te rog introdu un cod de test');
      return;
    }

    setTestError('');

    try {
      const response = await fetch(`${BASE_URL}/api/tests/take/${testCode}`);
      const data = await response.json();

      if (response.status === 410 || !response.ok) {
        setTestError('Codul testului este invalid sau expirat');
        return;
      }

      // Cache both test data and test code
      localStorage.setItem('currentTest', JSON.stringify(data));
      localStorage.setItem('testCode', testCode.trim());
      
      navigate('/student-details');

    } catch (err) {
      setTestError('Codul testului este invalid sau expirat');
    }
  };

  const renderAuthButton = () => {
    if (isLoading) {
      return (
        <button className="bg-yellow-400/50 text-purple-900 font-bold py-2 px-6 rounded-full cursor-wait">
          Se încarcă...
        </button>
      );
    }

    if (isAuthenticated) {
      return (
        <button 
          onClick={() => navigate('/teacher')}
          className="bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-2 px-6 rounded-full transition transform hover:scale-105"
        >
          Panou Profesor
        </button>
      );
    }

    return (
      <button 
        onClick={() => navigate('/login')}
        className="bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-2 px-6 rounded-full transition transform hover:scale-105"
      >
        Autentificare
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-indigo-500 text-white">
      <header className="container mx-auto py-6 px-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Brain className="h-8 w-8" />
          <span className="text-2xl font-bold">PMind</span>
        </div>
        <nav>
          <ul className="flex space-x-6">
            <li><a href="#" className="hover:text-yellow-300 transition">Funcționalități</a></li>
            <li><a href="#" className="hover:text-yellow-300 transition">Cum Funcționează</a></li>
            <li><a href="#" className="hover:text-yellow-300 transition">Testimoniale</a></li>
          </ul>
        </nav>
        {renderAuthButton()}
      </header>

      <section className="container mx-auto px-4 py-20 flex flex-col md:flex-row items-center">
        <div className="md:w-1/2 mb-10 md:mb-0">
          <h1 className="text-5xl font-bold leading-tight mb-6">
            Management de Proiect <span className="text-yellow-300">Simplificat</span> și Distractiv!
          </h1>
          <p className="text-xl mb-8">
            PMind transformă conceptele complexe de management de proiect în aventuri captivante de învățare. Stăpânește PM-ul în timp ce te bucuri de călătorie!
          </p>
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="w-full sm:w-auto relative flex flex-col">
              <input
                type="text"
                placeholder="Introdu codul testului"
                className="w-full sm:w-64 py-3 px-6 rounded-l-full text-purple-900 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
              />
              {testError && (
                <div className="absolute -bottom-12 left-0 text-red-500 text-sm font-medium mt-2">
                  {testError}
                </div>
              )}
            </div>
            <button 
              onClick={handleStartTest}
              className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-3 px-8 rounded-r-full transform transition hover:scale-105 shadow-lg flex items-center justify-center"
            >
              Începe Testul <ChevronRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="md:w-1/2 flex justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-3 max-w-md">
            <div className="bg-purple-100 rounded-xl p-4">
              <h3 className="text-purple-800 font-bold text-lg mb-2">Provocare PM</h3>
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="text-gray-800 font-medium">Care NU face parte din Triunghiul Constrângerilor în Managementul de Proiect?</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => handleAnswerClick(0)}
                  disabled={showFeedback}
                  className={getButtonClassName(0)}
                >
                  Timp
                </button>
                <button 
                  onClick={() => handleAnswerClick(1)}
                  disabled={showFeedback}
                  className={getButtonClassName(1)}
                >
                  Cost
                </button>
                <button 
                  onClick={() => handleAnswerClick(2)}
                  disabled={showFeedback}
                  className={getButtonClassName(2)}
                >
                  Conținut
                </button>
                <button 
                  onClick={() => handleAnswerClick(3)}
                  disabled={showFeedback}
                  className={getButtonClassName(3)}
                >
                  Risc
                </button>
              </div>
              {showFeedback && (
                <div className={`mt-4 p-3 rounded-lg text-center ${selectedAnswer === 3 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {selectedAnswer === 3 ? 
                    "Corect! Risc nu face parte din Triunghiul Constrângerilor." : 
                    "Greșit! Risc este răspunsul corect. Triunghiul Constrângerilor constă în Timp, Cost și Conținut."
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features section */}
      <section className="bg-white/10 backdrop-blur-lg py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">De Ce Studenții PM <span className="text-yellow-300">Aleg</span> PMind</h2>
          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-center transform transition hover:scale-105 hover:bg-white/30">
              <div className="bg-yellow-400 p-4 rounded-full inline-flex mb-4">
                <Target className="h-8 w-8 text-purple-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">Învățare Interactivă</h3>
              <p className="text-white/90">
                Practică concepte PM prin scenarii captivante și simulări din lumea reală.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-center transform transition hover:scale-105 hover:bg-white/30">
              <div className="bg-yellow-400 p-4 rounded-full inline-flex mb-4">
                <BookOpen className="h-8 w-8 text-purple-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">Traseu Personalizat</h3>
              <p className="text-white/90">
                Învață în ritmul tău cu planuri de studiu personalizate și conținut adaptiv.
              </p>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-center transform transition hover:scale-105 hover:bg-white/30">
              <div className="bg-yellow-400 p-4 rounded-full inline-flex mb-4">
                <Award className="h-8 w-8 text-purple-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">Urmărește Progresul</h3>
              <p className="text-white/90">
                Obține certificate și medalii în timp ce îți monitorizezi călătoria de învățare.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-purple-900/30 backdrop-blur-lg py-10 mt-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-6 md:mb-0">
              <Brain className="h-6 w-6" />
              <span className="text-xl font-bold">PMind</span>
            </div>
            <div className="flex space-x-8 mb-6 md:mb-0">
              <a href="#" className="hover:text-yellow-300 transition">Despre</a>
              <a href="#" className="hover:text-yellow-300 transition">Confidențialitate</a>
              <a href="#" className="hover:text-yellow-300 transition">Termeni</a>
              <a href="#" className="hover:text-yellow-300 transition">Contact</a>
            </div>
            <div className="text-sm text-white/70">
              © 2025 PMind. Toate drepturile rezervate.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
